import { useState } from 'react'
import {
  DateRangeOption,
  dateRangeOptionToRange
} from '../../legacy-design-system/date-picker'
import { DateRange } from '../../legacy-design-system/date-picker/shared'

export interface DateFilter<DateField> {
  readonly start: string
  readonly end: string
  readonly field?: DateField
}

export interface DateRangeChildProps<DateField> {
  readonly dateRange?: DateRange
  readonly dateRangeOption?: DateRangeOption
  readonly dateFilter?: DateFilter<DateField>
  readonly dateField?: DateField
  readonly onSetCustomDateRange: (dateRange: DateRange) => void
  readonly onSetDateRangeOption: (dateRangeOption: DateRangeOption) => void
  readonly onClearDateRange: () => void
  readonly onSetDateField: (dateField: DateField) => void
}

export function useDateRange<DateField = {}>(
  defaultDateField?: DateField
): DateRangeChildProps<DateField> {
  const [dateRange, setDateRange] = useState<DateRange>()
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>()
  const [dateField, setDateField] = useState<DateField | undefined>(
    defaultDateField
  )

  function onSetCustomDateRange(newDateRange: DateRange) {
    if (newDateRange.start.getTime() >= newDateRange.end.getTime()) {
      throw new Error('start must be less than end')
    }

    setDateRange(newDateRange)
    setDateRangeOption(DateRangeOption.CUSTOM)
  }

  function onSetDateRangeOption(newDateRangeOption: DateRangeOption) {
    setDateRangeOption(newDateRangeOption)
    setDateRange(
      dateRangeOption === DateRangeOption.CUSTOM
        ? dateRange
        : optionToRange(newDateRangeOption)
    )
  }

  function optionToRange(option): DateRange {
    return dateRangeOptionToRange[option]()
  }

  function onClearDateRange() {
    setDateRange(undefined)
    setDateRangeOption(undefined)
  }

  function onSetDateField(newDateField: DateField) {
    setDateField(newDateField)
  }

  function getDateFilter(): DateFilter<DateField> | undefined {
    if (dateRange) {
      return {
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
        field: dateField
      }
    }
  }

  return {
    dateRange,
    dateRangeOption,
    dateField,
    onSetCustomDateRange,
    onSetDateRangeOption,
    onClearDateRange,
    onSetDateField,
    dateFilter: getDateFilter()
  }
}
